import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, ShoppingBasket } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { scrollToSection } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import UserMenu, { SignInButton } from "@/components/UserMenu";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Shop", id: "catalog" },
  { label: "Why Us", id: "why-us" },
  { label: "Our Story", id: "story" },
  { label: "Bulk Enquiry", id: "enquiry" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (id) => {
    setMenuOpen(false);
    if (id === "contact") {
      if (pathname === "/contact") scrollToSection("contact-top");
      else navigate("/contact");
      return;
    }
    if (pathname !== "/") { navigate(`/#${id}`); return; }
    scrollToSection(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 border-b border-amber-900/10 bg-cream/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 sm:h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8">
        <button onClick={() => go("hero")} className="text-left" data-testid="nav-home-link">
          <LogoFull />
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              data-testid={`nav-${item.id}-link`}
              className="font-body text-sm font-medium text-ragi-dark/80 transition-colors hover:text-terracotta"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <span className="hidden md:inline-flex items-center rounded-full border border-saffron/40 bg-saffron-subtle px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-saffron-dark" data-testid="tagline-badge">
            Made with Love by Kandulas
          </span>
          <UserMenu />
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-open-button"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-ragi text-cream transition-transform hover:scale-105"
            aria-label="Open basket"
          >
            <ShoppingBasket className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1 text-[11px] font-bold text-white" data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </button>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-ragi/20 text-ragi-dark" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <SheetDescription className="sr-only" data-testid="mobile-menu-description">Kandula Home Foods shop, story and contact navigation.</SheetDescription>
              <div className="mt-10 flex flex-col gap-6">
                {NAV.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => go(item.id)}
                    data-testid={`mobile-nav-${item.id}-link`}
                    className="text-left font-display text-2xl font-semibold text-ragi-dark"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="mt-2 border-t border-cream-muted pt-6" data-testid="mobile-menu-account">
                  {user ? (
                    <>
                      <p className="text-sm font-semibold text-ragi-dark" data-testid="mobile-menu-user-name">{user.name}</p>
                      <Link to="/account/orders" onClick={() => setMenuOpen(false)} className="mt-3 block text-sm text-ragi-dark/80" data-testid="mobile-menu-orders-link">My Orders</Link>
                      {user.is_admin && <Link to="/admin" onClick={() => setMenuOpen(false)} className="mt-2 block text-sm text-ragi-dark/80" data-testid="mobile-menu-admin-link">Owner Dashboard</Link>}
                      <button onClick={() => { setMenuOpen(false); logout(); }} className="mt-3 text-sm font-semibold text-terracotta" data-testid="mobile-menu-logout">Sign out</button>
                    </>
                  ) : (
                    <SignInButton label="Sign in with Google" testId="mobile-google-signin-button" />
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
